import { EFFECTS, ADVANCED_PRESETS } from "../../lib/textEffects";
import { WARP_LIST } from "../../lib/textWarp";
import { COLOR_SWATCHES } from "../../lib/constants";
import { Slider } from "../ui/slider";
import { useSubscription } from "../../context/SubscriptionContext";
import { Crown } from "@phosphor-icons/react";

const Label = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">{children}</p>
);

// Tiny preview of an effect on "Ag"
function effectPreview(id) {
  const base = { fontFamily: "Outfit, sans-serif", fontWeight: 800, color: "#fff", fontSize: 20, lineHeight: 1 };
  switch (id) {
    case "shadow": return { ...base, textShadow: "1.5px 1.5px 1.5px rgba(0,0,0,0.6)" };
    case "lift": return { ...base, textShadow: "0 1px 3px rgba(0,0,0,0.7)" };
    case "glow": return { ...base, textShadow: "0 0 6px #3b82f6" };
    case "echo": return { ...base, textShadow: "2px 2px 0 rgba(59,130,246,0.5)" };
    case "outline": return { ...base, color: "#fff", WebkitTextStroke: "1px #3b82f6", paintOrder: "stroke fill" };
    case "hollow": return { ...base, color: "transparent", WebkitTextStroke: "1px #fff" };
    case "splice": return { ...base, color: "transparent", WebkitTextStroke: "1px #fff", textShadow: "2px 2px 0 #3b82f6" };
    case "neon": return { ...base, color: "#fff", textShadow: "0 0 2px #fff, 0 0 6px #ec4899" };
    case "glitch": return { ...base, textShadow: "1.5px 0 #ff00c1, -1.5px 0 #00fff9" };
    case "background": return { ...base, background: "#3b82f6", padding: "1px 4px", borderRadius: 3 };
    default: return base;
  }
}

export default function TextEffects({ el, onUpdate, onStart, onApplyPreset }) {
  const current = el.effect || "none";
  const meta = EFFECTS.find((e) => e.id === current) || {};
  const strokes = Array.isArray(el.strokes) ? el.strokes : [];
  const { isPro, openUpgrade } = useSubscription();

  const addStroke = () => {
    if (!isPro) { openUpgrade("Text strokes & outlines are a Pro feature. Upgrade to add bold, layered outlines to your text."); return; }
    onStart();
    const palette = ["#000000", "#ffffff", "#ff3ea5", "#22d3ee", "#ffd23f"];
    const next = palette[strokes.length % palette.length];
    onUpdate({ strokes: [...strokes, { color: next, width: strokes.length === 0 ? 4 : 6 }] });
  };
  const updateStroke = (i, patch) => {
    onStart();
    onUpdate({ strokes: strokes.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  };
  const removeStroke = (i) => {
    onStart();
    onUpdate({ strokes: strokes.filter((_, idx) => idx !== i) });
  };

  return (
    <div data-testid="text-effects-section">
      {/* ---- Stroke / Outline (multi) ---- */}
      <div className="flex items-center justify-between mb-2">
        <Label>Stroke</Label>
        <button
          onClick={addStroke}
          data-testid="add-stroke-btn"
          className="flex items-center gap-1 rounded-md border border-white/15 bg-[#0E0E0E] hover:border-blue-500/60 hover:bg-blue-600/10 px-2 py-1 text-[11px] font-semibold text-zinc-200 transition-all"
        >
          <span className="text-sm leading-none">+</span> Add stroke {!isPro && <Crown weight="fill" size={11} className="text-amber-300" />}
        </button>
      </div>
      {strokes.length === 0 ? (
        <p className="text-[10px] text-zinc-500 mb-4">Add an outline around your text. Stack multiple strokes for layered borders.</p>
      ) : (
        <div className="space-y-2.5 mb-4">
          {strokes.map((s, i) => (
            <div key={i} className="rounded-lg border border-white/10 bg-[#0E0E0E] p-2.5" data-testid={`stroke-row-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <label className="relative h-7 w-7 rounded-md border border-white/15 overflow-hidden cursor-pointer shrink-0" style={{ background: s.color || "#000000" }}>
                  <input type="color" value={s.color || "#000000"} onMouseDown={onStart} onChange={(e) => updateStroke(i, { color: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer" data-testid={`stroke-color-${i}`} />
                </label>
                <span className="text-[11px] text-zinc-400 flex-1">Stroke {i + 1}</span>
                <span className="text-[11px] text-zinc-300 tabular-nums w-9 text-right">{s.width || 1}px</span>
                <button onClick={() => removeStroke(i)} data-testid={`stroke-remove-${i}`} className="h-6 w-6 rounded-md border border-white/10 text-zinc-400 hover:text-white hover:border-red-500/50 hover:bg-red-600/10 transition-all flex items-center justify-center leading-none">×</button>
              </div>
              <Slider value={[s.width || 1]} min={1} max={55} step={1} onPointerDown={onStart} onValueChange={(v) => updateStroke(i, { width: v[0] })} data-testid={`stroke-width-${i}`} />
            </div>
          ))}
        </div>
      )}

      <Label>Effects</Label>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {EFFECTS.map((e) => (
          <button
            key={e.id}
            onClick={() => { onStart(); onUpdate({ effect: e.id }); }}
            data-testid={`effect-${e.id}`}
            className={`rounded-lg border p-2 flex flex-col items-center justify-center gap-1 h-16 transition-all ${current === e.id ? "border-blue-500 bg-blue-600/15" : "border-white/10 bg-[#0E0E0E] hover:border-white/25"}`}
          >
            <div className="h-6 flex items-center justify-center" style={{ background: e.id === "none" || e.id === "background" ? "transparent" : "#18181b", borderRadius: 4, width: "100%" }}>
              {e.id === "none" ? <span className="text-zinc-500 text-base font-bold">Ag</span> : <span style={effectPreview(e.id)}>Ag</span>}
            </div>
            <span className="text-[9px] text-zinc-400">{e.label}</span>
          </button>
        ))}
      </div>

      {meta.color && (
        <div className="mb-4">
          <Label>Effect color</Label>
          <div className="flex items-center gap-2 mb-2">
            <label className="relative h-8 w-8 rounded-lg border border-white/15 overflow-hidden cursor-pointer shrink-0" style={{ background: el.effectColor || "#000000" }}>
              <input type="color" value={el.effectColor || "#000000"} onMouseDown={onStart} onChange={(e) => onUpdate({ effectColor: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="effect-color-input" />
            </label>
            <div className="grid grid-cols-8 gap-1.5 flex-1">
              {COLOR_SWATCHES.slice(0, 16).map((c) => (
                <button key={c} onClick={() => { onStart(); onUpdate({ effectColor: c }); }} className="h-4 w-4 rounded border border-white/15 hover:scale-110 transition-transform" style={{ background: c }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {meta.intensity && (
        <div className="mb-4">
          <Label>Intensity</Label>
          <Slider value={[el.effectIntensity ?? 50]} min={0} max={100} step={1} onPointerDown={onStart} onValueChange={(v) => onUpdate({ effectIntensity: v[0] })} data-testid="effect-intensity-slider" />
        </div>
      )}

      <Label>Shape · Curve</Label>
      <div className="mb-2">
        <Slider value={[el.curve || 0]} min={-100} max={100} step={1} onPointerDown={onStart} onValueChange={(v) => onUpdate({ curve: v[0] })} data-testid="curve-slider" />
      </div>
      <p className="text-[10px] text-zinc-500 mb-4">Drag left or right to curve the text. Center is straight.</p>

      <Label>Warp text</Label>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {WARP_LIST.map((w) => (
          <button
            key={w.id}
            onClick={() => { onStart(); onUpdate({ warp: w.id }); }}
            data-testid={`warp-${w.id}`}
            className={`rounded-lg border px-2 py-2 text-[11px] font-medium transition-all ${(el.warp || "none") === w.id ? "border-blue-500 bg-blue-600/15 text-white" : "border-white/10 bg-[#0E0E0E] text-zinc-300 hover:border-white/25"}`}
          >
            {w.label}
          </button>
        ))}
      </div>
      {el.warp && el.warp !== "none" && (
        <div className="mb-4">
          <Label>Bend</Label>
          <Slider value={[el.warpBend ?? 50]} min={-100} max={100} step={1} onPointerDown={onStart} onValueChange={(v) => onUpdate({ warpBend: v[0] })} data-testid="warp-bend-slider" />
        </div>
      )}

      <Label>Advanced styles</Label>
      <div className="grid grid-cols-2 gap-2">
        {ADVANCED_PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => { onStart(); onApplyPreset(p.patch); }}
            data-testid={`advanced-${p.id}`}
            className="rounded-lg border border-white/10 bg-[#0E0E0E] hover:border-blue-500/40 px-2 py-2.5 text-xs font-medium transition-all"
            style={{ fontFamily: `'${p.patch.fontFamily}', sans-serif` }}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}
